import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Divider, Form, Modal, notification, Skeleton } from 'antd';

import { Helmet } from 'react-helmet';
import { useEffect } from 'react';
import { isEmpty } from 'lodash';
import { PageProps } from '@/types/PageProps';

import { DeleteButton, SaveButton } from '@/components/_common/Buttons';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import DirectSaleForm from '@/components/Movements/DirectSale/DirectSaleForm';
import {
  useDeleteDirectSale,
  useUpdateDirectSale,
} from '@/core/domain/sales/DirectSale/DirectSale.hooks';
import {
  DirectSaleFormValues,
  DirectSaleStatusEnum,
  UpdateDirectSaleRequest,
} from '@/core/domain/sales/DirectSale/DirectSale.types';
import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';
import { useDirectSaleContext } from '../../DirectSaleProvider';
import { useDisclosure } from '@/hooks';
import Authorities from '@/types/Authority';
import DirectSaleButtons from '@/components/Movements/DirectSale/DirectSaleButtons';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useInvoices } from '@/core/domain/invoice/invoice/invoice.hooks';
import { InvoiceOriginReferenceEntity } from '@/core/domain/invoice/invoices/invoices.enums';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import DirectSaleLGPDTermModal from '@/components/Movements/DirectSale/DirectSaleLGPDTermModal';
import { DateTimeFormat } from '@/utils/moment';
import moment from 'moment';
import { hasPermissions } from '@/utils/functions/hasPermissions';

function DirectSaleGeneralInfoPage({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();
  const { directSaleId, directSale, isFetching, isAlerted, isLoading } =
    useDirectSaleContext();
  const [openLgpd, { open: openLgpdModal, close: closeLgpdModal }] =
    useDisclosure(false);

  const [form] = Form.useForm<DirectSaleFormValues>();

  const updateDirectSale = useUpdateDirectSale();
  const deleteDirectSale = useDeleteDirectSale();

  const { data, isLoading: invoceLoading } = useInvoices(
    {
      entity: InvoiceOriginReferenceEntity.SALES,
      entityId: directSale?.id,
      entityCode: directSale?.saleCode,
      invoiceType: undefined,
    },
    !!directSale,
  );

  const invoiceData = flatInfiniteData(data);

  const { setParamLabel } = useBreadcrumbs();

  useEffect(() => {
    if (directSale && !isEmpty(directSale)) {
      form.setFieldsValue({
        ...directSale,
        items: directSale.items.map((item) => ({
          id: item.id,
          discount: item.discount ? item.discount * 100 : 0,
          discountCost: item.discountCurrency ? item.discountCurrency : 0,
          partId: item?.part?.id,
          totalStock: item.quantity,
          unitCost: item.unitCost,
          unitaryValue: item.unitPrice,
          manufacturingCode: item?.part?.manufacturingCode,
          sku: item?.part?.sku,
          manufacturerId: item?.manufacturer?.id,
          description: item?.part?.name,
        })),
        situation: directSale.status,
        saleDate: directSale.saleDate
          ? moment.utc(directSale.saleDate)
          : undefined,
        authorizationDate: directSale.authorizationDate
          ? moment.utc(directSale.authorizationDate)
          : undefined,
        cancellationDate: directSale.cancellationDate
          ? moment.utc(directSale.cancellationDate)
          : undefined,
      });

      setParamLabel(':directSaleId', directSale.saleCode);
    }
  }, [directSale, isFetching]);

  const handleSaveClick = async () => {
    try {
      const { items, ...values } = await form.validateFields();

      const obj: UpdateDirectSaleRequest = {
        id: Number(directSaleId),
        ...values,
        items: items.map((item) => ({
          id: item.id,
          discount: item.discount / 100,
          partId: item?.partId,
          quantity: item.totalStock,
          unitCost: item.unitCost,
          unitPrice: item.unitaryValue,
          manufacturerId: item?.manufacturerId,
        })),
        note: values.note ?? '',
        tagId: directSale?.saleTag?.id,
        saleDate: values.saleDate?.format(DateTimeFormat),
        authorizationDate: values.authorizationDate?.format(DateTimeFormat),
      };

      await updateDirectSale.mutateAsync(obj);
    } catch (err) {
      notification.error({
        message: intl.formatMessage({
          id: 'commons.form.notification.error',
        }),
        description: intl.formatMessage({
          id: 'commons.form.notification.errorDescription',
        }),
      });
    }
  };

  const handleRemoveClick = () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'directSale.notifications.remove.confirm.title',
      }),
      content: `${intl.formatMessage({
        id: 'directSale.notifications.remove.confirm.content',
      })}`,
      onOk: async () => {
        await deleteDirectSale.mutateAsync(Number(directSaleId));

        history.push(`/app/movements/directSale`);
      },
      okType: 'danger',
    });
  };

  return (
    <>
      <Helmet
        title={intl.formatMessage({
          id: 'layout.menu.movements.directSale.details.general',
        })}
      />
      <DefaultRouteTabs
        route={route.parent}
        permissions={route.permissions}
        baseKey={directSaleId}
        basePath={`/app/movements/directSale/${directSaleId}`}
        extra={
          <SpaceForButton>
            <SaveButton
              type="primary"
              block
              onClick={isAlerted ? openLgpdModal : handleSaveClick}
              loading={updateDirectSale.isLoading}
              disabled={!hasPermissions([Authorities.MOVIMENTATION_SALE_EDIT])}
            >
              <FormattedMessage id="commons.entity.save" />
            </SaveButton>

            {(directSale?.status === DirectSaleStatusEnum.OPEN ||
              (directSale?.status === DirectSaleStatusEnum.CANCELED &&
                hasPermissions([
                  Authorities.MOVIMENTATION_SALE_DELETE_CANCELED,
                ]))) && (
              <DeleteButton
                block
                onClick={handleRemoveClick}
                disabled={
                  !hasPermissions([Authorities.MOVIMENTATION_SALE_DELETE])
                }
              >
                <FormattedMessage id="commons.entity.remove" />
              </DeleteButton>
            )}
          </SpaceForButton>
        }
      >
        <Skeleton loading={isLoading || invoceLoading}>
          <DirectSaleForm
            directSale={directSale}
            form={form}
            invoiceData={invoiceData}
          />

          <Divider />

          <DirectSaleButtons
            directSale={directSale}
            invoiceData={invoiceData}
          />
        </Skeleton>

        {directSale && (
          <DirectSaleLGPDTermModal
            customerId={directSale.customerId}
            onClose={closeLgpdModal}
            open={openLgpd}
            onCallback={handleSaveClick}
          />
        )}
      </DefaultRouteTabs>
    </>
  );
}

export default DirectSaleGeneralInfoPage;
